<template>
  <Modal :open="open" @close="close">
    <div v-if="localPlanSale">
      <h1 class="text-center font-extrabold text-xl">{{ t("title") }}</h1>

      <div>
        <p class="font-bold text-xl pb-2 tracking-tight text-gray-900">{{ t("section_contract") }}</p>
        <FormField id="planSaleDate" class="mt-1" :label="t('date')" type="date" v-model="localPlanSale.date" />
        <FormField
          id="planSaleExpirationDate"
          class="mt-1"
          :label="t('expiration_date')"
          type="date"
          v-model="localPlanSale.expirationDate"
        />
      </div>

      <div class="mt-4">
        <p class="font-bold text-xl pb-2 tracking-tight text-gray-900">{{ t("section_customer") }}</p>
        <FormField
          id="planSaleCustomerEmail"
          class="mt-1"
          :label="t('email')"
          type="email"
          v-model="localPlanSale.customer.email"
        />
        <FormField
          id="planSaleCustomerLastName"
          class="mt-2"
          :label="t('last_name')"
          type="text"
          v-model="localPlanSale.customerInformation.lastName"
        />
        <FormField
          id="planSaleCustomerFirstName"
          class="mt-2"
          :label="t('first_name')"
          type="text"
          v-model="localPlanSale.customerInformation.firstName"
        />
        <FormField
          id="planSaleCustomerPhone"
          class="mt-2"
          :label="t('phone')"
          type="tel"
          v-model="localPlanSale.customerInformation.phone"
        />
        <FormField
          id="planSaleCustomerBirthDate"
          class="mt-2"
          :label="t('birth_date')"
          type="date"
          v-model="localPlanSale.customerInformation.birthDate"
        />
        <GoogleAddress class="mt-2" :initialAddress="customerAddress" @newAddress="updateCustomerAddress" />
      </div>

      <div v-if="localPlanSale.planSaleProduct" class="mt-4">
        <p class="font-bold text-xl pb-2 tracking-tight text-gray-900">{{ t("section_products") }}</p>
        <div v-for="product in localPlanSale.planSaleProduct" :key="product.id" class="mt-2">
          <FormField
            :id="`planSaleProductTitle-${product.id}`"
            class="mt-1"
            :label="t('product_title')"
            type="text"
            v-model="product.title"
          />
          <FormField
            :id="`planSaleProductReferenceId-${product.id}`"
            class="mt-2"
            :label="t('product_reference_id')"
            type="text"
            v-model="product.referenceId"
          />
          <FormField
            v-if="!isVirtualProduct(product)"
            :id="`planSaleProductPrice-${product.id}`"
            class="mt-2"
            :label="t('product_price')"
            type="number"
            v-model="product.price"
          />
          <FormField
            :id="`planSaleProductPurchaseDate-${product.id}`"
            class="mt-2"
            :label="t('product_purchase_date')"
            type="date"
            v-model="product.purchaseDate"
            :error="isFutureDate(product.purchaseDate) ? t('purchase_date_in_future') : null"
          />
        </div>
      </div>

      <div class="mt-6 flex flex-col items-center space-y-4 text-center">
        <p class="text-sm text-gray-700 max-w-md">{{ t("warning") }}</p>
        <GenericButton :disabled="hasFuturePurchaseDate" @click="submit">{{ t("submit") }}</GenericButton>
      </div>
    </div>
  </Modal>
</template>

<script>
import { Modal, GenericButton, FormField, GoogleAddress, isVirtualProduct } from "@estaly/ui";
import { useI18n } from "vue-i18n";

export default {
  name: "UpdateModal",

  components: { Modal, GenericButton, FormField, GoogleAddress },

  props: {
    open: {
      type: Boolean,
      default: false,
    },
    planSale: {
      type: Object,
      required: true,
    },
  },

  emits: ["close", "update-plan-sale"],

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      localPlanSale: this.buildLocalPlanSale(),
    };
  },

  computed: {
    hasFuturePurchaseDate() {
      return (this.localPlanSale.planSaleProduct || []).some((product) => this.isFutureDate(product.purchaseDate));
    },

    customerAddress() {
      const address = this.planSale.customer?.address;

      return {
        address: [address?.address1, address?.city, address?.province, address?.country].filter(Boolean).join(", "),
      };
    },
  },

  watch: {
    open(isOpen) {
      if (isOpen) this.localPlanSale = this.buildLocalPlanSale();
    },
  },

  methods: {
    isVirtualProduct,

    buildLocalPlanSale() {
      const customer = this.planSale.customer;

      return {
        date: this.toInputDate(this.planSale.date),
        expirationDate: this.toInputDate(this.planSale.expirationDate),
        customer: { email: customer?.email },
        customerInformation: {
          firstName: customer?.firstName,
          lastName: customer?.lastName,
          phone: customer?.phone,
          birthDate: this.toInputDate(customer?.birthDate),
          address1: customer?.address?.address1,
          city: customer?.address?.city,
          province: customer?.address?.province,
          country: customer?.address?.country,
          zipCode: customer?.address?.zipCode,
        },
        planSaleProduct: this.planSale.products?.map((product) => ({
          id: product.id,
          title: product.title,
          referenceId: product.referenceId,
          price: product.price,
          purchaseDate: this.toInputDate(product.purchaseDate),
          productNature: product.productNature,
        })),
      };
    },

    updateCustomerAddress(newAddress) {
      this.localPlanSale.customerInformation = {
        ...this.localPlanSale.customerInformation,
        address1: newAddress.street,
        city: newAddress.city,
        province: newAddress.province,
        country: newAddress.country,
        zipCode: newAddress.zipCode,
      };
    },

    submit() {
      if (this.hasFuturePurchaseDate) return;

      this.$emit("update-plan-sale", this.localPlanSale);
    },

    close() {
      this.$emit("close");
    },

    isFutureDate(date) {
      // Dates are "yyyy-MM-dd" strings, so they compare lexicographically
      const now = new Date();
      const today = [now.getFullYear(), now.getMonth() + 1, now.getDate()]
        .map((part) => String(part).padStart(2, "0"))
        .join("-");
      return Boolean(date) && date > today;
    },

    // "dd/mm/yyyy" => "yyyy-MM-dd"
    toInputDate(date) {
      const parts = date?.split("/");
      return parts?.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : null;
    },
  },
};
</script>

<i18n src="./UpdateModal.i18n.json"></i18n>
